import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { UpdateFormDataActionWithId } from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { Country } from '@app/finxone-web-frontend/app/lib/dtos/countries.dto';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import {
  OrganisationState,
  OrganisationStateModel,
} from '@app/finxone-web-frontend/app/lib/state/organisation.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { countryCodeToCircleFlag } from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-to-flag.utils';
import {
  BaseCssProperties,
  ProfileStateModel,
  ProviderClass,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { FormActionTypeEnum } from '../../../../../../../../../libs/shared/enums/form-action.enum';
import { Observable, debounceTime, map, takeUntil } from 'rxjs';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { FormSubmitter } from '../../form-widgets/form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-edit-address',
  templateUrl: './edit-address.component.html',
  styleUrls: ['./edit-address.component.scss'],
})
export class EditAddressComponent extends FormSubmitter implements OnInit {
  @Output() formValuesChange = new EventEmitter<any>();
  @Input() formActionStateData: { [key: string]: string };
  @Input() dynamicZone = false;
  public zone = '';
  public countries: Country[];
  public countryList: { title: string; currencyFlag: string; key: string }[];
  public editAddressForm: FormGroup;
  @Select(OrganisationState.getOrganisation)
  organisation$: Observable<OrganisationStateModel>;
  @Select(ProfileState.getProfile)
  profile$!: Observable<ProfileStateModel>;
  public profileData: any;
  public reviewDetailsButtonStyle: baseCssConfigStyle = {};
  public organisationData: OrganisationStateModel;
  public formStateData: any;
  public showButton = false;
  public isSubmitting = true;
  public customInputFieldStyle: baseCssConfigStyle;
  public inputFieldBaseStyle: baseCssConfigStyle;
  public inputFieldInactiveStyle: baseCssConfigStyle;
  public currentActiveStyle: baseCssConfigStyle;
  public currentInactiveStyle: baseCssConfigStyle;
  public widgetConfig: UiZoneWidgetConfig;
  public inputFieldAddressStreetStyle: baseCssConfigStyle;
  public inputFieldAddressPostCodeStyle: baseCssConfigStyle;
  public inputFieldAddressCountryStyle: baseCssConfigStyle;
  public inputFieldAddressStateStyle: baseCssConfigStyle;
  public inputFieldAddressCityStyle: baseCssConfigStyle;
  public colorTextAlignStyle: baseCssConfigStyle;
  public textInputFontStyle: baseCssConfigStyle;

  constructor(
    public pageConfig: PageConfigurationProvider,
    public dataIn: ProviderClass<any>,
    private configService: ConfigService,
    public override store: Store,
    private formSubmissionService: FormSubmissionService,
    private formBuilder: FormBuilder,
    private urlZoneInput: ZonePageUrlProvider,
  ) {
    super(store);
    this.contentStyle = this.configService.onGetBaseWidgetStyle(this.dataIn.attri);

    this.configService
      .getCountries()
      .pipe(
        takeUntil(this.destroy$),
        map((response: Country[]) => {
          this.countries = response;
          this.countryList = this.countries.map(({ name, code }) => ({
            title: name,
            currencyFlag: this.getCountryFlag(code, '24px'),
            key: code,
          }));
        }),
      )
      .subscribe();

    this.organisation$.subscribe((organisation) => {
      this.organisationData = organisation;
    });

    this.profile$
      .pipe(
        takeUntil(this.destroy$),
        map((response: any) => {
          this.profileData = response;
        }),
      )
      .subscribe();
    this.zone = this.urlZoneInput.zoneUrlInput;
  }

  ngOnInit(): void {
    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
    }
    this.colorTextAlignStyle = {
      ...this.configService.getSpecificBaseCssStyle(this.attri, [
        BaseCssProperties.TEXT_BOLD,
        BaseCssProperties.TEXT_ITALIC,
        BaseCssProperties.TEXT_UNDERLINE,
        BaseCssProperties.TEXTALIGN,
        BaseCssProperties.V_ALIGN,
        BaseCssProperties.TEXT_FONT_STYLE,
      ]),
    };
    this.textInputFontStyle = this.configService.getSpecificBaseCssStyle(this.dataIn.attri, [
      BaseCssProperties.TEXT_INPUT_FONT_SIZE,
    ]);

    this.createForm();
    this.buildForm();
    this.styleUpdate();
    if (this.dynamicZone && this.formActionStateData !== undefined) {
      this.formStateData = this.formActionStateData;
      this.patchDynamicZoneAddressForm();
    }
  }

  public getCountryFlag(countryCode: string, widthValue: string): string {
    return countryCodeToCircleFlag(countryCode, widthValue);
  }

  private styleUpdate() {
    const { baseStyle, activeStyle, inactiveStyle } = this.configService.getInputFieldCssStyles(
      this.attri?.widgetProperties,
    );
    this.customInputFieldStyle = baseStyle;
    this.inputFieldBaseStyle = activeStyle;
    this.inputFieldInactiveStyle = inactiveStyle;
    this.currentInactiveStyle = {
      ...this.inputFieldInactiveStyle,
      ...this.inputFieldBaseStyle,
    };
    this.currentActiveStyle = {
      ...this.customInputFieldStyle,
      ...this.inputFieldBaseStyle,
    };

    this.inputFieldAddressStreetStyle = this.currentInactiveStyle;
    this.inputFieldAddressPostCodeStyle = this.currentInactiveStyle;
    this.inputFieldAddressCountryStyle = this.currentInactiveStyle;
    this.inputFieldAddressStateStyle = this.currentInactiveStyle;
    this.inputFieldAddressCityStyle = this.currentInactiveStyle;
  }

  public updateInputFieldStyles(event: boolean, type: string) {
    switch (type) {
      case 'addressStreet':
        this.inputFieldAddressStreetStyle = event ? this.currentActiveStyle : this.currentInactiveStyle;
        break;
      case 'addressCity':
        this.inputFieldAddressCityStyle = event ? this.currentActiveStyle : this.currentInactiveStyle;
        break;
      case 'addressState':
        this.inputFieldAddressStateStyle = event ? this.currentActiveStyle : this.currentInactiveStyle;
        break;
      case 'addressCountry':
        this.inputFieldAddressCountryStyle = event ? this.currentActiveStyle : this.currentInactiveStyle;
        break;
      case 'addressPostCode':
        this.inputFieldAddressPostCodeStyle = event ? this.currentActiveStyle : this.currentInactiveStyle;
        break;
    }
  }
  private createForm(): void {
    this.editAddressForm = this.formBuilder.group({
      addressCountry: new FormControl('', Validators.required),
      address1: new FormControl('', Validators.required),
      address2: new FormControl(''),
      address3: new FormControl(''),
      address4: new FormControl(''),
      addressCity: new FormControl('', Validators.required),
      addressPostCode: new FormControl('', Validators.required),
      addressState: new FormControl('', Validators.required),
    });
    this.formSubmissionService.addCurrentPageFormGroups(this.editAddressForm);
  }
  private buildForm(): void {
    this.subscribeToFormAction().subscribe((response) => {
      this.formStateData = response?.formData;
    });
    if (this.zone === 'organisation-verification') {
      this.patchAddressForm();
    }
    if (this.zone === 'verification') {
      this.patchKYCEditAddressForm();
    }
    this.editAddressForm.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        map((formValues) => {
          this.formValuesChange.emit(formValues);

          if (this.editAddressForm.valid && !this.dynamicZone) {
            this.store.dispatch(
              new UpdateFormDataActionWithId(
                {
                  ...formValues,
                  addressCountry: formValues.addressCountry.key,
                },
                this.zone === 'verification'
                  ? FormActionTypeEnum.KYC_DETAILS
                  : FormActionTypeEnum.KYB_DETAILS,
              ),
            );
          }
        }),
      )
      .subscribe();
  }

  private patchDynamicZoneAddressForm(): void {
    const formStateCountryValue = this.formStateData?.['addressCountry'];
    const selectedCountryFormState = this.findSelectedCountry(formStateCountryValue);

    this.editAddressForm.patchValue({
      addressCountry: selectedCountryFormState || '',
      address1: this.formStateData?.['addressStreet'] || '',
      addressCity: this.formStateData?.['addressCity'] || '',
      addressPostCode: this.formStateData?.['addressPostCode'] || '',
      addressState: this.formStateData?.['addressState'] || '',
    });
  }

  private patchAddressForm(): void {
    const countryValue = this.organisationData?.address?.country;
    const formStateCountryValue = this.formStateData?.['addressCountry'];

    const selectedCountry = this.findSelectedCountry(countryValue);
    const selectedCountryFormState = this.findSelectedCountry(formStateCountryValue);

    this.editAddressForm.patchValue({
      addressCountry: selectedCountryFormState || selectedCountry,
      address1: this.formStateData?.['address1'] ?? this.organisationData?.address?.address1,
      address2: this.formStateData?.['address2'] ?? this.organisationData?.address?.address2,
      address3: this.formStateData?.['address3'] ?? this.organisationData?.address?.address3,
      address4: this.formStateData?.['address4'] ?? this.organisationData?.address?.address4,
      addressCity: this.formStateData?.['addressCity'] || this.organisationData?.address?.city,
      addressPostCode: this.formStateData?.['addressPostCode'] || this.organisationData?.address?.postalCode,
      addressState: this.formStateData?.['addressState'] || this.organisationData?.address?.state,
    });
  }

  private patchKYCEditAddressForm(): void {
    const countryValue = this.profileData?.country;
    const formStateCountryValue = this.formStateData?.['addressCountry'];

    const selectedCountry = this.findSelectedCountry(countryValue);
    const selectedCountryFormState = this.findSelectedCountry(formStateCountryValue);

    this.editAddressForm.patchValue({
      addressCountry: selectedCountryFormState || selectedCountry,
      address1: this.formStateData?.['address1'] || this.profileData?.address1,
      address2: this.formStateData?.['address2'] || this.profileData?.address2,
      address3: this.formStateData?.['address3'] || this.profileData?.address3,
      address4: this.formStateData?.['address4'] || this.profileData?.address4,
      addressCity: this.formStateData?.['addressCity'] || this.profileData?.city,
      addressPostCode: this.formStateData?.['addressPostCode'] || this.profileData?.postalCode,
      addressState: this.formStateData?.['addressState'] || this.profileData?.county,
    });
  }

  private findSelectedCountry(countryValue: string): any {
    return countryValue ? this.countryList.find((country) => country.key === countryValue) ?? '' : '';
  }

  public valueChanges() {
    this.isSubmitting = true;
  }

  private submitData() {
    const formValues: Record<string, any> = this.editAddressForm.value;
    if (this.isSubmitting && this.editAddressForm.valid) {
      if (
        formValues['addressCountry'] &&
        typeof formValues['addressCountry'] === 'object' &&
        'key' in formValues['addressCountry']
      ) {
        const countryKey = formValues['addressCountry']['key'];
        formValues['addressCountry'] = countryKey;
      }
      this.formSubmissionService.submitIndividualForm(
        {
          addressCountry: formValues['addressCountry'],
          addressStreet: formValues['addressStreet'],
          addressCity: formValues['addressCity'],
          addressPostCode: formValues['addressPostCode'],
          addressState: formValues['addressState'],
        },
        '',
      );
      this.isSubmitting = false;
    } else {
      this.editAddressForm.markAllAsTouched();
    }
  }
}
